import { Env } from '@env';

type PixelParams = Record<string, unknown>;

// Meta's fixed set of standard events. These must be sent via `fbq('track')`
// so Meta recognizes them as conversions (optimization/attribution). Any other
// name is a custom event and goes through `fbq('trackCustom')`.
const STANDARD_EVENTS = new Set<string>([
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
]);

// PII we can feed the Pixel for Advanced Matching. Values are normalized (not
// hashed) here; the Pixel hashes them with SHA-256 client-side before sending.
type AdvancedMatching = {
  email?: string | null;
  phone?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  externalId?: string | number | null;
};

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : undefined;
}

// fbevents.js writes _fbp only once it has loaded over the network, so the first
// event of a session can fire before the cookie exists. Poll for it up to this
// long so the server CAPI call carries the same browser id as the Pixel.
const FBP_WAIT_MS = 2000;
const FBP_POLL_INTERVAL_MS = 50;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// Digits-only E.164, defaulting a bare 10-digit number to US (+1). Mirrors the
// backend CAPI normalization so the Pixel and CAPI match on the same value.
function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';

  return digits.length === 10 ? `1${digits}` : digits;
}

// Build the fbq Advanced Matching object, dropping blank fields. Returns
// undefined when there's nothing worth sending.
function buildMatchingData(
  user: AdvancedMatching
): Record<string, string> | undefined {
  const data: Record<string, string> = {};

  const em = user.email?.trim().toLowerCase();
  const ph = user.phone ? normalizePhone(user.phone) : '';
  const fn = user.firstname?.trim().toLowerCase();
  const ln = user.lastname?.trim().toLowerCase();
  const externalId = user.externalId?.toString().trim();

  if (em) data.em = em;
  if (ph) data.ph = ph;
  if (fn) data.fn = fn;
  if (ln) data.ln = ln;
  if (externalId) data.external_id = externalId;

  return Object.keys(data).length > 0 ? data : undefined;
}

// Web Meta Pixel (fbq). Initialized once on load (mirrors mixpanel/index.ts:
// disabled in development). Conversion events take a shared `eventId` that the
// server CAPI call reuses so Meta dedupes on event_name + event_id.
class MetaPixelService {
  private ready = false;
  // Latest Advanced Matching data + a key to avoid re-initializing with the
  // same values on every event.
  private userData?: Record<string, string>;
  private userDataKey?: string;

  constructor() {
    if (Env.APP_ENV === 'development') return;
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    this.init();
  }

  private init() {
    if (this.ready) return;
    if (!Env.META_PIXEL_ID) return;

    /* eslint-disable */
    // prettier-ignore
    (function (f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    // Include any Advanced Matching we already have so PageView + subsequent
    // events carry hashed PII for a better Event Match Quality score.
    if (this.userData) {
      (window as any).fbq('init', Env.META_PIXEL_ID, this.userData);
    } else {
      (window as any).fbq('init', Env.META_PIXEL_ID);
    }
    (window as any).fbq('track', 'PageView');
    this.ready = true;
  }

  // Enables Advanced Matching. Pass normalized (unhashed) PII; the Pixel hashes
  // it client-side before sending. This is the main lever for Event Match
  // Quality. Safe to call repeatedly — it no-ops when the data is unchanged.
  setUserData(user: AdvancedMatching) {
    const data = buildMatchingData(user);
    if (!data) return;

    const key = JSON.stringify(data);
    if (key === this.userDataKey) return;

    this.userData = data;
    this.userDataKey = key;

    if (!Env.META_PIXEL_ID) return;
    this.fbq?.('init', Env.META_PIXEL_ID, data);
  }

  private get fbq(): ((...args: any[]) => void) | undefined {
    if (typeof window === 'undefined') return undefined;
    return (window as any).fbq;
  }

  // Fires a Pixel event. Standard Meta events (e.g. "Lead",
  // "CompleteRegistration", "SubmitApplication") use `track` so Meta treats
  // them as conversions; everything else (e.g. the Mixpanel-mirrored
  // "Page Viewed") uses `trackCustom`. The optional `eventId` is shared with
  // the server CAPI call for dedup.
  track(event: string, customData: PixelParams = {}, eventId?: string) {
    const method = STANDARD_EVENTS.has(event) ? 'track' : 'trackCustom';

    if (eventId) {
      this.fbq?.(method, event, customData, { eventID: eventId });
    } else {
      this.fbq?.(method, event, customData);
    }
  }

  // Raw _fbp / _fbc cookies for server-side CAPI dedup + match quality.
  // _fbc is only present when a real fbclid hit the URL — we never fabricate it.
  getMatchKeys(): { fbp?: string; fbc?: string } {
    return { fbp: readCookie('_fbp'), fbc: readCookie('_fbc') };
  }

  // Same as getMatchKeys, but waits out the _fbp cookie race. Resolves as soon
  // as the cookie lands (usually well under the cap) and gives up quietly on
  // timeout so a blocked or slow Pixel never holds a CAPI event hostage.
  async waitForMatchKeys(): Promise<{ fbp?: string; fbc?: string }> {
    // Pixel disabled (development, or no id configured): nothing will ever set
    // the cookie, so don't stall the CAPI call waiting for it.
    if (!this.ready) return this.getMatchKeys();

    const deadline = Date.now() + FBP_WAIT_MS;
    while (!readCookie('_fbp') && Date.now() < deadline) {
      await sleep(FBP_POLL_INTERVAL_MS);
    }

    return this.getMatchKeys();
  }
}

const metaPixel = new MetaPixelService();
export default metaPixel;
